import React, { useState, useEffect } from 'react';
import './Home.scss';
import { IHomeProps } from './IHomeProps';
import { Button, Checkbox, Dropdown, DropdownOption, TextArea } from '@orderly/morrisons-component-library';
import Table, { isHeaderSorted } from '../../../components/Table/Table';
import { navigateTo } from '../../../routing/Navigation';
import { IStoreState, IReport, OrderRetailerType } from '../../../types';
import { Dispatch } from 'redux';
import { getAdminRetailersRequest } from '../../../actions/Admin/adminRetailersAction';
import { connect } from 'react-redux';
import LoadingThrobber from '../../../components/LoadingThrobber/LoadingThrobber';
import { IConfirmedOrdersParams } from '../../../api/Reports/ConfirmedOrders/IConfirmedOrdersParams';
import {
  getConfirmedOrdersReportRequest,
  resetReportData,
} from '../../../actions/Reports/confirmedOrdersAction';
import { getNormalizedEnumString, getNumericalKeys } from '../../../helpers/EnumHelper';
import PersonalDetailsCard from '../../../components/PersonalDetailsCard/PersonalDetailsCard';
import ActionsCard from '../../../components/ActionsCard/ActionsCard';
import ReportsCard from '../../../components/ReportsCard/ReportsCard';
import { GenerateDownload } from '../../../helpers/DownloadHelper';
import ImportsCard from '../../../components/ImportsCard/ImportsCard';
import ContactUsCard from '../../../components/ContactUsCard/ContactUsCard';
import { getRegionsRequest } from '../../../actions/SupplyChain/regionsAction';

const Home = ({
  userDetails,
  retailers,
  regions,
  regionsLoading,
  getRegions,
  getAdminRetailers,
  confirmedOrdersCsvDownload,
  downloadConfirmedOrdersCsv,
  clearConfirmedOrdersCsvDownload,
}: IHomeProps) => {
  const [retailerDetails, setRetailerDetails] = useState([]);
  const [sortOrder, setSortOrder] = useState(0);
  const [filteredType, setFilteredType] = useState<number>(0);
  const [checkedRegions, setCheckedRegions] = useState<number[]>([]);

  useEffect(() => {
    getAdminRetailers();
    getRegions();
  }, []);

  const getLoadedRetailers = () => {
    if (!retailers.loading && retailers.data && !regionsLoading && regions) {
      const mergedRetailers = retailers.data.retailers.map((retailer) => {
        let orderStats = retailers.data.orderStats.find(stats => stats.id === retailer.id);

        if (!orderStats) {
          orderStats = {
            id: retailer.id,
            confirmedOrderCount: 0,
            submittedOrderCount24Hours: 0,
            submittedOrderCount7Days: 0,
          };
        }

        return {
          ...retailer,
          ...orderStats,
        };
      });

      if (userDetails.regionId !== -1) {
        const regionFilteredRetailers = mergedRetailers.filter(
          r => r.stores.some(store => store.region && store.region.id === userDetails.regionId),
        );

        return regionFilteredRetailers;
      }

      return mergedRetailers;
    }
  };

  useEffect(() => {
    setRetailerDetails(getLoadedRetailers);
  }, [retailers]);

  useEffect(() => {
    if (retailers.data) {
      filterRetailers();
    }
  }, [checkedRegions]);

  useEffect(() => {
    if (confirmedOrdersCsvDownload && confirmedOrdersCsvDownload.data) {
      createConfirmedOrderDownload(confirmedOrdersCsvDownload.data);
    }
  }, [confirmedOrdersCsvDownload]);

  const typeOptions: number[] = getNumericalKeys(OrderRetailerType);

  const downloadConfirmedOrderCsv = async (retailerId: number) => {
    try {
      await downloadConfirmedOrdersCsv({ retailerId, orderType: null });
    } catch (error) {
      console.log('An error has occurred', error);
    }
  };

  const onRegionChecked = (id: number, checked: boolean) => {
    let newCheckedRegions: number[];

    if (checked) {
      newCheckedRegions = [...checkedRegions, id];
    } else {
      newCheckedRegions = checkedRegions.filter(x => x !== id);
    }

    setCheckedRegions(newCheckedRegions);
  };

  const createConfirmedOrderDownload = ({ filename, content }: IReport): void => {
    GenerateDownload(content, filename);
    clearConfirmedOrdersCsvDownload();
  };

  const filterRetailers = (filterType?: number) => {
    let loadedRetailers = getLoadedRetailers();
    const appliedType = filterType !== undefined ? filterType : filteredType;

    if (checkedRegions.length > 0) {
      loadedRetailers = loadedRetailers.filter(
        retailer => retailer.stores.some(store => checkedRegions.includes(store.region && store.region.id)),
      );
    }

    const sorted = sortRetailers(loadedRetailers, sortOrder);
    if (appliedType === 0) {
      setRetailerDetails(sorted);
    } else {
      setRetailerDetails(sorted.filter(x => Number(x.meta.OrderType) === appliedType));
    }
    setFilteredType(appliedType);
  };

  const sortRetailers = (retailers: any[], orderBy: number): any[] => {
    const sortedRetailers = [...retailers];
    const descending = sortOrder % 2 !== 0;
    switch (orderBy) {
      case 0:
      case 1:
        sortedRetailers.sort((a: any, b: any) =>
          a.externalIdentifier.localeCompare(b.externalIdentifier),
        );
        break;
      case 2:
      case 3:
        sortedRetailers.sort((a: any, b: any) => a.confirmedOrderCount - b.confirmedOrderCount);
        break;
      case 4:
      case 5:
        sortedRetailers.sort(
          (a: any, b: any) => a.submittedOrderCount24Hours - b.submittedOrderCount24Hours,
        );
        break;
      case 6:
      case 7:
        sortedRetailers.sort(
          (a: any, b: any) => a.submittedOrderCount7Days - b.submittedOrderCount7Days,
        );
        break;
    }

    if (descending) {
      sortedRetailers.reverse();
    }

    return sortedRetailers;
  };

  const getOrderByProperty = (sortOrder: number): { property: string; descending: boolean } => {
    let property = undefined;
    switch (sortOrder) {
      case 0:
      case 1:
        property = 'retailer';
        break;
      case 2:
      case 3:
        property = 'confirmedOrderCount';
        break;
      case 4:
      case 5:
        property = 'submittedOrderCount24Hours';
        break;
      case 6:
      case 7:
        property = 'submittedOrderCount7Days';
        break;
    }

    return { property, descending: sortOrder % 2 !== 0 };
  };

  const populateTableHeader = (): JSX.Element => {
    const { property, descending } = getOrderByProperty(sortOrder);
    return (
      <thead>
        <tr>
          <th>Retailer {isHeaderSorted('retailer', property, descending)}</th>
          <th>
            Retailer Type
            {
              userDetails.regionId === -1 && (
                <select
                  value={filteredType}
                  onChange={newValue => filterRetailers(Number(newValue.target.value))}
                  className="retailer-type-dropdown"
                >
                  <option value="0">All</option>
                  {typeOptions.map(key =>
                    <option key={key} value={key}>{OrderRetailerType[key]}</option>,
                  )}
                </select>
              )
            }
          </th>
          <th>Confirmed Orders {isHeaderSorted('confirmedOrderCount', property, descending)}</th>
          <th>
            Submitted (24hrs) {isHeaderSorted('submittedOrderCount24Hours', property, descending)}
          </th>
          <th>
            Submitted (7days) {isHeaderSorted('submittedOrderCount7Days', property, descending)}
          </th>
        </tr>
      </thead>
    );
  };

  return (
    <div className="account-manager-home">
      <div className="sidebar">
        <PersonalDetailsCard />
        <ActionsCard />
        <ReportsCard retailers={retailers} />
        <ImportsCard />
        <ContactUsCard />
      </div>
      <div className="content card">
        <h4>Retailer Management</h4>
        <div className="retailers-heading">
          <div className="orderRetailers">
            <p>Manage your Retailer's users and documents, or view confirmed orders</p>
            <Dropdown
              className="retailers-sort"
              label=""
              name="sortRetailers"
              onChange={(value: DropdownOption) => {
                setSortOrder(value.value as number);
              }}
              selectedValue={sortOrder}
              options={[
                { label: 'RETAILER: A-Z', value: 0 },
                { label: 'RETAILER: Z-A', value: 1 },
                { label: 'CONFIRMED ORDERS: low to high', value: 2 },
                { label: 'CONFIRMED ORDERS: high to low', value: 3 },
                { label: 'SUBMITTED (24HRS): low to high', value: 4 },
                { label: 'SUBMITTED (24HRS): high to low', value: 5 },
                { label: 'SUBMITTED (7DAYS): low to high', value: 6 },
                { label: 'SUBMITTED (7DAYS): high to low', value: 7 },
              ]}
            />
          </div>
          {userDetails.regionId === -1 &&
            <div className="filterRegion">
              <p>Filter by stores in region</p>
              <div className="region-checkboxes">
                {
                  regions.map(x =>
                    <Checkbox
                      checked={checkedRegions.includes(x.id)}
                      key={x.id}
                      label={getNormalizedEnumString(x.name)}
                      name={x.id.toString()}
                      onChange={(_, checked) => onRegionChecked(x.id, checked)}
                      isSmall={true}
                    />,
                  )
                }
              </div>
            </div>
          }
        </div>
        {
          <Table className="retailers">
            {populateTableHeader()}
            <tbody>
              {
                (retailers.loading || regionsLoading) &&
                <tr>
                  <td colSpan={4}><LoadingThrobber /></td>
                </tr> ||
                retailerDetails && sortRetailers(retailerDetails, sortOrder).map((retailer) => {
                  return (
                    <tr key={retailer.id}>
                      <td>{retailer.externalIdentifier}</td>
                      <td>{OrderRetailerType[retailer.meta.OrderType]}</td>
                      <td>{retailer.confirmedOrderCount}</td>
                      <td>{retailer.submittedOrderCount24Hours}</td>
                      <td>{retailer.submittedOrderCount7Days}</td>
                      <td className="actions">
                        <span title="Download confirmed orders">
                          <Button
                            className="confirmed-orders"
                            icon="icon-stats-bars"
                            type="button"
                            text=""
                            onClick={() => downloadConfirmedOrderCsv(retailer.id)}
                          />
                        </span>
                        <span title="Manage users">
                          <Button
                            className="manage-users"
                            icon="icon-users"
                            type="button"
                            text=""
                            onClick={() => navigateTo(`/account-manager/users?retailer=${retailer.id}`)}
                          />
                        </span>
                        <span title="Manage Retailer Pricing">
                          <Button
                            className="manage-retailer-pricing"
                            icon="icon-offer"
                            type="button"
                            text=""
                            onClick={() => navigateTo(`/account-manager/retailer-pricing/${retailer.id}`)}
                            disabled={parseInt(retailer.meta.OrderType, 10) !== OrderRetailerType.Bulk}
                          />
                        </span>
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </Table>
        }
        <Button
          type="button"
          className="secondary confirmed-orders"
          text="Download All Confirmed Orders"
          onClick={() => downloadConfirmedOrderCsv(null)}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStoreState) => {
  return {
    userDetails: state.userDetails,
    retailers: state.adminRetailers,
    confirmedOrdersCsvDownload: state.confirmedOrders,
    regions: state.regions.regions,
    regionsLoading: state.regions.loading,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    getAdminRetailers: () => dispatch(getAdminRetailersRequest()),
    downloadConfirmedOrdersCsv: (parameters: IConfirmedOrdersParams) =>
      dispatch(getConfirmedOrdersReportRequest(parameters)),
    clearConfirmedOrdersCsvDownload: () => dispatch(resetReportData()),
    getRegions: () => dispatch(getRegionsRequest()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
